import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { PerkWizardFormData } from "./PerkWizard";

interface ValueCostStepProps {
  form: UseFormReturn<PerkWizardFormData>;
}

export function ValueCostStep({ form }: ValueCostStepProps) {
  const valueType = form.watch("valueType");

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="valueType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Value Type</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select value type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Percentage">Percentage Discount</SelectItem>
                <SelectItem value="FixedAmount">Fixed Amount Discount</SelectItem>
                <SelectItem value="FreeItem">Free Item</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {valueType === "Percentage" && (
        <FormField
          control={form.control}
          name="discountValue"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Discount Percentage</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    placeholder="20"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {valueType === "FixedAmount" && (
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="discountValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Discount Amount</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    placeholder="50"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || "AED"}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {valueType === "FreeItem" && (
        <FormField
          control={form.control}
          name="freeItemDescription"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Free Item Description</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Free coffee with any purchase" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="coinCost"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Coin Cost</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={0}
                placeholder="0"
                {...field}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            </FormControl>
            <FormDescription>
              How many coins employees must spend to unlock this perk. Set to 0 for free perks.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
